// Verify Ed25519 signatures from the auth-server. Used by the daemon when
// fetching allow-list updates — the auth-server signs each response with
// its private signing key, and the daemon pinned the matching public key
// at link time.
//
// Implemented with `node:crypto`'s native Ed25519 support (available since
// Node 15.7). No external crypto dep needed; the relay's tweetnacl is
// X25519/XSalsa20 only and doesn't cover Ed25519 signing.

import { createPublicKey, verify as edVerify } from "node:crypto";

/**
 * Verify an Ed25519 signature.
 *
 * @param message  Canonical message bytes the signature covers.
 * @param signature Signature bytes (Ed25519 produces 64-byte sigs).
 * @param serverPubkeyHex  Pinned public key from /daemon/link response.
 * @returns true if the signature is valid; false on any error or mismatch.
 */
export function verifyAuthServerSignature(
  message: Buffer | string,
  signature: Buffer,
  serverPubkeyHex: string,
): boolean {
  try {
    // Reconstruct the SPKI DER for an Ed25519 public key:
    // 12-byte fixed prefix `302a300506032b6570032100` + 32 raw key bytes.
    const pubBytes = Buffer.from(serverPubkeyHex, "hex");
    if (pubBytes.length !== 32) return false;
    const spkiPrefix = Buffer.from("302a300506032b6570032100", "hex");
    const spki = Buffer.concat([spkiPrefix, pubBytes]);
    const key = createPublicKey({ key: spki, format: "der", type: "spki" });

    const data = typeof message === "string" ? Buffer.from(message, "utf8") : message;
    return edVerify(null, data, key, signature);
  } catch {
    return false;
  }
}

/**
 * Build the canonical message string the auth-server signs for an allow-list
 * response. Must match `routes/daemon.ts` in `@appostle/auth-server` exactly.
 *
 *   v1\n<signedAt>\n<pubkey1>\n<pubkey2>\n...
 */
export function canonicalAllowlistMessage(args: {
  signedAt: string;
  pubkeys: readonly string[];
}): string {
  return ["v1", args.signedAt, ...args.pubkeys].join("\n");
}
